import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Card, CardContent,
  IconButton, Typography, Divider, Box, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BugReportIcon from '@mui/icons-material/BugReport';
import './ReleaseNotesDialog.css';

function ReleaseNotesDialog({ open, onClose, activeTheme, releaseNotes }) {
  const [relNotes, setRelNotes] = useState({});

  useEffect(() => {
    setRelNotes(releaseNotes);
  }, [releaseNotes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: activeTheme.palette.background.paper,
          color: activeTheme.palette.text.primary,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle className="release-dialog-title" sx={{ color: activeTheme.palette.text.primary, pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
              Release Update
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip 
                label={`v${relNotes.version}`}
                size="small"
                sx={{
                  backgroundColor: activeTheme.palette.mode === 'dark' 
                    ? 'rgba(25, 118, 210, 0.2)' 
                    : 'rgba(25, 118, 210, 0.1)',
                  color: activeTheme.palette.mode === 'dark' 
                    ? '#64b5f6' 
                    : '#1976d2',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Typography className="release-version" sx={{ color: activeTheme.palette.text.secondary, fontSize: '0.875rem' }}>
                {relNotes.releaseDate}
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="close-release-button"
            sx={{ 
              color: activeTheme.palette.text.primary,
              '&:hover': {
                backgroundColor: activeTheme.palette.action.hover,
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider className="release-divider" sx={{ mt: 2 }} />
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {relNotes.releaseLog?.features?.length > 0 && (
          <Box className="release-section" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircleOutlineIcon 
                sx={{ 
                  color: activeTheme.palette.mode === 'dark' ? '#66bb6a' : '#2e7d32',
                  fontSize: '1.5rem'
                }} 
              />
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: activeTheme.palette.text.primary }}>
                Improvements
              </Typography>
            </Box>
            <Box className="release-list-container">
              {relNotes.releaseLog.features.map((note, index) => (
                <Box key={index} className="release-list-item">
                  <Box display="flex" gap={1.5} alignItems="flex-start">
                    <Box 
                      className="release-bullet"
                      sx={{
                        backgroundColor: activeTheme.palette.mode === 'dark' ? '#66bb6a' : '#2e7d32',
                        minWidth: '6px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        mt: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <Box flex={1}>
                      <Typography 
                        variant="subtitle2" 
                        component="div" 
                        sx={{ 
                          fontWeight: 600, 
                          color: activeTheme.palette.text.primary,
                          mb: 0.5
                        }}
                      >
                        {note.feature}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: activeTheme.palette.text.secondary,
                          lineHeight: 1.6
                        }}
                      >
                        {note.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {relNotes.releaseLog?.bugFixes?.length > 0 && (
          <Box className="release-section">
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <BugReportIcon 
                sx={{ 
                  color: activeTheme.palette.mode === 'dark' ? '#ef5350' : '#c62828',
                  fontSize: '1.5rem'
                }} 
              />
              <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: activeTheme.palette.text.primary }}>
                Bug Fixes
              </Typography>
            </Box>
            <Box className="release-list-container">
              {relNotes.releaseLog.bugFixes.map((note, index) => (
                <Box key={index} className="release-list-item">
                  <Box display="flex" gap={1.5} alignItems="flex-start">
                    <Box 
                      className="release-bullet"
                      sx={{
                        backgroundColor: activeTheme.palette.mode === 'dark' ? '#ef5350' : '#c62828',
                        minWidth: '6px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        mt: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <Box flex={1}>
                      <Typography 
                        variant="subtitle2" 
                        component="div" 
                        sx={{ 
                          fontWeight: 600, 
                          color: activeTheme.palette.text.primary,
                          mb: 0.5
                        }}
                      >
                        {note.feature}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: activeTheme.palette.text.secondary,
                          lineHeight: 1.6
                        }}
                      >
                        {note.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReleaseNotesDialog;